import * as React from 'react';
import { GripVertical } from 'lucide-react';
import {
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  closestCorners,
  getFirstCollision,
  useSensor,
  useSensors,
  type CollisionDetection,
  type KeyboardCoordinateGetter,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { cn } from '@/lib/utils';

/**
 * Drag-to-reorder, built for a till.
 *
 * The library choice is forced by the hardware. HTML5 drag-and-drop — the
 * `draggable` attribute and `dragstart`/`drop` — does not fire on a
 * touchscreen at all, so a list reordered that way is decorative on every
 * device this product actually runs on. `@dnd-kit` listens to **pointer**
 * events, which the same code path gives us for mouse, pen and finger, and it
 * ships a keyboard sensor so the same reorder is reachable without dragging
 * anything.
 *
 * Everything here is direction-agnostic: the sorting is vertical, the handle is
 * placed by flex order rather than by a physical side, so an Arabic tree reads
 * right-to-left without a second code path.
 */

/**
 * Both sensors, wired the way a touch device needs.
 *
 * `PointerSensor` covers mouse, pen and touch from one listener. The 6px
 * activation distance is what separates a drag from a tap: without it, every
 * tap on the handle would start a drag and the row's own buttons would fight
 * the gesture. The keyboard sensor is not optional — a reorder that only
 * happens under a finger is unreachable for anyone who cannot drag.
 */
export function useReorderSensors(
  coordinateGetter: KeyboardCoordinateGetter = sortableKeyboardCoordinates,
) {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter }),
  );
}

export interface DragHandleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Names the row, because "drag handle" alone tells a screen reader nothing. */
  label: string;
  dragging?: boolean;
}

/**
 * The grip a row is dragged by.
 *
 * A handle rather than the whole row, for two reasons that both come from this
 * screen: the row already carries a checkbox, a name button and an action
 * strip, and a drag that started anywhere would swallow all of them; and a
 * bulk-selection checkbox living on the same row must stay a separate target,
 * so a tick is never a half-started drag.
 *
 * `touch-action: none` is the line that makes touch work. Without it the
 * browser claims the gesture for scrolling and the pointer stream stops
 * arriving mid-drag — the handle is small and dedicated, so the rest of the row
 * still scrolls the list normally.
 */
export const DragHandle = React.forwardRef<HTMLButtonElement, DragHandleProps>(
  ({ label, dragging, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      style={{ touchAction: 'none' }}
      className={cn(
        // Full row height: 44px of finger for a 16px icon.
        'flex h-11 w-8 shrink-0 touch-none items-center justify-center rounded-lg',
        'text-ink-muted hover:bg-hover',
        dragging ? 'cursor-grabbing' : 'cursor-grab',
        'disabled:cursor-not-allowed disabled:opacity-30',
        className,
      )}
      {...props}
    >
      <GripVertical className="h-4 w-4" aria-hidden />
    </button>
  ),
);
DragHandle.displayName = 'DragHandle';

/** True when `candidate` is a legal drop target for `active`. */
export type IsPeer = (active: UniqueIdentifier, candidate: UniqueIdentifier) => boolean;

/**
 * Collision detection that can only ever land on a peer.
 *
 * In a tree, "peer" is "same parent". Filtering here rather than rejecting the
 * drop in `onDragEnd` is the difference between a gap that opens where the row
 * can go and a gap that opens where it cannot: the operator is told by the
 * layout, before letting go, instead of by nothing at all afterwards.
 */
export function peerCollision(isPeer: IsPeer): CollisionDetection {
  return (args) =>
    closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((entry) =>
        isPeer(args.active.id, entry.id),
      ),
    });
}

/**
 * Keyboard movement restricted to peers.
 *
 * `sortableKeyboardCoordinates` walks every droppable in the `DndContext`
 * regardless of which `SortableContext` it belongs to, so in a tree the arrow
 * keys would happily step a root category into somebody else's child list —
 * a move the drop handler then refuses, leaving the keys looking broken. This
 * is the same algorithm with the candidate list narrowed first.
 */
export function peerKeyboardCoordinates(isPeer: IsPeer): KeyboardCoordinateGetter {
  return (
    event,
    { context: { active, collisionRect, droppableContainers, droppableRects, over } },
  ) => {
    if (event.code !== 'ArrowDown' && event.code !== 'ArrowUp') return undefined;
    event.preventDefault();
    if (!active || !collisionRect) return undefined;

    const down = event.code === 'ArrowDown';
    const candidates = droppableContainers.getEnabled().filter((entry) => {
      if (!entry || entry.disabled || !isPeer(active.id, entry.id)) return false;
      const rect = droppableRects.get(entry.id);
      if (!rect) return false;
      return down ? collisionRect.top < rect.top : collisionRect.top > rect.top;
    });

    const collisions = closestCorners({
      active,
      collisionRect,
      droppableRects,
      droppableContainers: candidates,
      pointerCoordinates: null,
    });
    let closestId = getFirstCollision(collisions, 'id');
    if (closestId === over?.id && collisions.length > 1) closestId = collisions[1].id;
    if (closestId == null) return undefined;

    const rect = droppableRects.get(closestId);
    // Rows in both lists are one line tall, so the target's own top-left is the
    // resting place; there is no height difference to correct for.
    return rect ? { x: rect.left, y: rect.top } : undefined;
  };
}
